import React, { useEffect, useMemo, useState } from "react";
import actionPhotos_data from "../../assets/actionPhotos_data";
import landscapePhotos_data from "../../assets/landscapePhotos_data";
import eventPhotos_data from "../../assets/eventPhotos_data";
import {
  MotionItem,
  MotionSection,
  MotionStagger,
} from "../../motion/primitives";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import {
  GALLERY_HASH_TO_TAB,
  GALLERY_TAB_NAV_EVENT,
} from "../../galleryNav";

const GallerySection = ({ images, title, sectionId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const prefersReducedMotion = useReducedMotion();

  const safeImages = useMemo(() => images || [], [images]);
  const current = safeImages[currentIndex];

  useEffect(() => {
    setCurrentIndex(0);
  }, [sectionId]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % safeImages.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? safeImages.length - 1 : prevIndex - 1,
    );
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeImages.length]);

  if (!safeImages.length) return null;

  return (
    <div className="w-full max-w-[780px] mx-auto rounded-[10px] overflow-hidden bg-[#161513] my-4 sm:my-6">
      {/* Title */}
      <h3
        className="text-[24px] sm:text-[28px] md:text-[32px] font-montserrat text-center py-4 
        bg-gradient-to-r from-[#df8908] via-[#e98a2f] to-[#f6b14d] bg-clip-text text-transparent"
      >
        {title}
      </h3>

      {/* Gallery Container */}
      <div className="rounded-[10px]">
        {/* Main Image Container */}
        <div className="relative w-full h-[52vw] max-h-[460px] min-h-[220px] sm:h-[46vw] md:h-[430px] rounded-[14px] overflow-hidden bg-white/5 border border-white/10">
          {/* Blurred backdrop to avoid harsh side-black */}
          <div className="pointer-events-none absolute inset-0">
            <img
              src={current.w_img}
              alt=""
              className="h-full w-full object-cover blur-2xl scale-110 opacity-55"
            />
            <div className="absolute inset-0 bg-[#161513]/20" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.14),rgba(0,0,0,0.55))]" />
          </div>

          {/* Main Image */}
          <div className="w-full h-full flex items-center justify-center rounded-[10px]">
            <AnimatePresence mode="wait">
              <motion.img
                key={`${sectionId}-${currentIndex}`}
                src={current.w_img}
                alt=""
                className="relative z-10 w-full h-full object-contain rounded-[10px] will-change-transform drop-shadow-[0_20px_60px_rgba(0,0,0,0.35)]"
                initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
                animate={
                  prefersReducedMotion ? undefined : { opacity: 1, y: 0 }
                }
                exit={prefersReducedMotion ? undefined : { opacity: 0, y: -8 }}
                transition={
                  prefersReducedMotion
                    ? undefined
                    : { duration: 0.42, ease: [0.16, 1, 0.3, 1] }
                }
              />
            </AnimatePresence>
          </div>

          {/* Navigation Controls */}
          <div className="absolute inset-0 z-20 flex items-center justify-between p-2 sm:p-4">
            <button
              className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full 
                       bg-white/10 text-white backdrop-blur-md border border-white/15
                       flex items-center justify-center transition duration-300
                       hover:bg-white/15 hover:scale-110 active:scale-95
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-[#df8908] focus-visible:ring-offset-2 focus-visible:ring-offset-[#161513]"
              onClick={handlePrev}
              type="button"
              aria-label="Previous photo"
            >
              <FiChevronLeft className="text-[22px] md:text-[26px]" />
            </button>

            <button
              className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full 
                       bg-white/10 text-white backdrop-blur-md border border-white/15
                       flex items-center justify-center transition duration-300
                       hover:bg-white/15 hover:scale-110 active:scale-95
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-[#df8908] focus-visible:ring-offset-2 focus-visible:ring-offset-[#161513]"
              onClick={handleNext}
              type="button"
              aria-label="Next photo"
            >
              <FiChevronRight className="text-[22px] md:text-[26px]" />
            </button>
          </div>

          {/* Index */}
          <div className="absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full px-3 py-1 text-[12px] sm:text-[13px] text-white/90 bg-black/35 backdrop-blur-md border border-white/10">
            {currentIndex + 1} / {safeImages.length}
          </div>
        </div>

        {/* Thumbnail Navigation - Now positioned below */}
        <div className="mt-2 backdrop-blur-sm p-0.5 sm:p-1 bg-[#161513]/60 rounded-[10px]">
          <div
            className="flex flex-wrap sm:flex-nowrap gap-1 sm:gap-2 justify-center sm:justify-center overflow-x-hidden sm:overflow-x-auto px-1 sm:px-2 py-0.5 max-w-full 
            [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            {safeImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                type="button"
                className={`flex-shrink-0 transition duration-300 w-8 sm:w-14 md:w-16 
                  ${
                    index === currentIndex
                      ? "ring-2 ring-[#df8908] opacity-100"
                      : "opacity-40 hover:opacity-100"
                  }`}
              >
                <img
                  src={image.w_img}
                  alt={`Thumbnail ${index + 1}`}
                  className="h-5 sm:h-10 md:h-12 w-8 sm:w-14 md:w-16 object-cover rounded-[6px]"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const GalleryPhotos = () => {
  const series = useMemo(
    () => [
      {
        id: "action",
        title: "Action",
        blurb:
          "Fast, sharp, and decisive—timed to the split-second. Snapshot of a moment in time.",
        images: actionPhotos_data,
      },
      {
        id: "event",
        title: "Event",
        blurb: "Energy, atmosphere, and real moments—captured as they happen.",
        images: eventPhotos_data,
      },
      {
        id: "landscape",
        title: "Landscape",
        blurb: "Wide, quiet, cinematic frames from the North and beyond.",
        images: landscapePhotos_data,
      },
    ],
    [],
  );

  const [activeId, setActiveId] = useState(() => {
    if (typeof window === "undefined") return series[0]?.id || "action";
    const raw = window.location.hash.replace(/^#/, "");
    return GALLERY_HASH_TO_TAB[raw] || series[0]?.id || "action";
  });
  const active = series.find((s) => s.id === activeId) || series[0];

  useEffect(() => {
    const syncTabFromHash = () => {
      const raw = window.location.hash.replace(/^#/, "");
      const tab = GALLERY_HASH_TO_TAB[raw];
      if (tab) setActiveId(tab);
    };
    syncTabFromHash();
    window.addEventListener("hashchange", syncTabFromHash);
    return () => window.removeEventListener("hashchange", syncTabFromHash);
  }, []);

  useEffect(() => {
    const onServiceNav = (e) => {
      const tabId = e.detail?.tabId;
      if (!tabId || !series.some((s) => s.id === tabId)) return;
      setActiveId(tabId);
    };
    window.addEventListener(GALLERY_TAB_NAV_EVENT, onServiceNav);
    return () =>
      window.removeEventListener(GALLERY_TAB_NAV_EVENT, onServiceNav);
  }, [series]);

  return (
    <MotionSection
      id="gallery"
      className="relative flex flex-col items-center justify-center gap-8 mx-2 my-10
        sm:gap-12 md:gap-16 lg:gap-20 
        sm:mx-[40px] md:mx-[70px] lg:mx-auto
        sm:my-12 md:my-20
        max-w-[1200px] w-full"
    >
      <div
        className="pointer-events-none absolute left-0 top-0 h-0 w-full overflow-visible"
        aria-hidden="true"
      >
        {Object.keys(GALLERY_HASH_TO_TAB).map((id) => (
          <span
            key={id}
            id={id}
            className="absolute left-0 top-0 block h-px w-px scroll-mt-[72px]"
          />
        ))}
      </div>
      <MotionStagger className="w-full flex flex-col items-center justify-center gap-8 sm:gap-12 md:gap-16 lg:gap-20">
        <MotionItem>
          <div className="relative w-full text-center">
            <h1
              className="text-[36px] font-semibold font-montserrat
              sm:text-[50px] md:text-[80px]"
            >
              Gallery
            </h1>
          </div>
        </MotionItem>

        <MotionItem>
          <div className="flex flex-col gap-6 md:gap-10 max-w-[900px] text-center mx-auto px-3 sm:px-0 w-[92%] sm:w-full">
            <p
              className="text-[16px] leading-7 text-[#d4d4d4] font-medium font-montserrat
              sm:text-[18px] sm:leading-8
              md:text-[22px] md:leading-9"
            >
              Welcome to my gallery. Here you will find a collection of my
              photography that reflects my work across various themes, including
              landscapes, events, and action. Each image showcases my approach
              to capturing moments and the beauty of the world around us. I
              invite you to take a moment to explore and enjoy…
            </p>
          </div>
        </MotionItem>

        <MotionItem>
          <div className="w-[92%] sm:w-full max-w-[780px] mx-auto">
            <div className="rounded-[14px] border border-white/10 bg-white/5 backdrop-blur-md p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div className="text-left">
                  <p className="text-[12px] tracking-[0.22em] uppercase text-white/60">
                    Series
                  </p>
                  <h2 className="text-[24px] sm:text-[28px] md:text-[34px] font-semibold font-montserrat">
                    <span className="bg-gradient-to-r from-[#df8908] via-[#e98a2f] to-[#f6b14d] bg-clip-text text-transparent">
                      {active?.title}
                    </span>
                  </h2>
                  <p className="text-[14px] sm:text-[15px] md:text-[16px] text-white/70 max-w-[56ch]">
                    {active?.blurb}
                  </p>
                </div>

                <div className="flex items-center justify-start sm:justify-end">
                  <div className="flex gap-2 overflow-x-auto max-w-full pr-1 py-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                    {series.map((s) => {
                      const isActive = s.id === activeId;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setActiveId(s.id)}
                          className={`relative px-4 py-2 rounded-full text-[14px] font-display font-medium transition
                          ${
                            isActive
                              ? "text-white bg-gradient-to-r from-[#df8908] via-[#e98a2f] to-[#f6b14d] shadow-[0_10px_30px_rgba(246,177,77,0.16)]"
                              : "text-white/80 bg-white/5 hover:bg-white/10 border border-white/10"
                          }
                          focus:outline-none focus-visible:ring-2 focus-visible:ring-[#df8908] focus-visible:ring-offset-2 focus-visible:ring-offset-[#161513]`}
                        >
                          {s.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <GallerySection
                title={`${active?.title} Series`}
                images={active?.images}
                sectionId={`series-${active?.id}`}
              />
            </div>
          </div>
        </MotionItem>
      </MotionStagger>
    </MotionSection>
  );
};

export default GalleryPhotos;
